import React, { useEffect, useRef, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Icon } from '@iconify/react'
import { colors, nativeSelect } from 'src/components/app-ui'
import { Badge } from 'src/components/user-access/components'
import {
  kokoroAmericanEnglishVoices,
  kokoroBritishEnglishVoices,
  kokoroJapaneseVoices,
  kokoroMandarinChineseVoices,
  kokoroSpanishVoices,
  kokoroFrenchVoices,
  kokoroItalianVoices,
  kokoroBrazilianPortugueseVoices,
  kokoroHindiVoices,
  languages,
} from 'src/components/settings-drawer/tabs/data/ttsVoices'

const BASIC_SAMPLE =
  'https://cdn.minicoursegenerator.com/assets/tts/basic/basic-sample.mp3'

const VOICES_BY_LANGUAGE = {
  'en-US': kokoroAmericanEnglishVoices,
  'en-GB': kokoroBritishEnglishVoices,
  ja: kokoroJapaneseVoices,
  zh: kokoroMandarinChineseVoices,
  es: kokoroSpanishVoices,
  fr: kokoroFrenchVoices,
  hi: kokoroHindiVoices,
  it: kokoroItalianVoices,
  pt: kokoroBrazilianPortugueseVoices,
}

export const voicesFor = language => VOICES_BY_LANGUAGE[language] || []

/** Normalizes the stored text-to-speech settings. */
export const readTextToSpeech = generalValues => {
  const tts = generalValues?.pageReaderSettings?.textToSpeech
  const language = tts?.language || languages[0].code
  return {
    value: Boolean(tts?.value ?? generalValues?.enablePageReader),
    mode: tts?.mode || 'basic',
    language,
    voice: tts?.voice || voicesFor(language)[0]?.value || '',
  }
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '14px 0',
  },
  heading: {
    fontSize: '15px',
    fontWeight: 500,
    color: colors.navy,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '20px 24px',
    borderRadius: '12px',
    background: '#FFFFFF',
  },
  pick: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: 0,
    border: 0,
    background: 'transparent',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
  },
  radio: {
    width: 22,
    height: 22,
    flex: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    boxSizing: 'border-box',
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
  },
  label: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '15px',
    fontWeight: 500,
    color: colors.navy,
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '12px',
    paddingLeft: 36,
  },
  desc: {
    fontSize: '14px',
    color: colors.muted,
  },
  selects: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    width: '100%',
  },
  select: {
    flex: 1,
    minWidth: 180,
    padding: '10px 12px',
    border: `1px solid ${colors.borderSoft}`,
    borderRadius: '8px',
    backgroundColor: '#FFFFFF',
    ...nativeSelect,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '14px',
    color: colors.navy,
  },
  play: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    border: `1px solid ${colors.navy}`,
    borderRadius: '6px',
    background: '#FFFFFF',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '14px',
    fontWeight: 500,
    color: colors.navy,
    '&:hover': {
      background: colors.hover,
    },
  },
})

/** Plays one sample at a time; returns [playingKey, toggle(key, url)]. */
const useSamplePlayer = () => {
  const audioRef = useRef(null)
  const [playing, setPlaying] = useState(null)

  useEffect(
    () => () => {
      if (audioRef.current) audioRef.current.pause()
    },
    [],
  )

  const toggle = (key, url) => {
    if (audioRef.current) audioRef.current.pause()
    if (playing === key) {
      setPlaying(null)
      return
    }
    audioRef.current = new window.Audio(url)
    audioRef.current.onended = () => setPlaying(null)
    audioRef.current.play()
    setPlaying(key)
  }

  return [playing, toggle]
}

/**
 * "Choose your TTS Type" cards. `onChange` receives the whole
 * textToSpeech object; `onUpgrade(event)` is called for locked options.
 */
const TtsSettings = ({ tts, onChange, canUseAdvanced, onUpgrade }) => {
  const classes = useStyles()
  const [playing, togglePlay] = useSamplePlayer()
  const voices = voicesFor(tts.language)

  const pickMode = (mode, event) => {
    if (mode === tts.mode) return
    if (mode === 'advanced' && !canUseAdvanced) {
      onUpgrade(event)
      return
    }
    const language = mode === 'advanced' ? tts.language : languages[0].code
    onChange({
      ...tts,
      mode,
      language,
      voice: mode === 'advanced' ? voicesFor(language)[0]?.value || '' : '',
    })
  }

  const options = [
    {
      mode: 'basic',
      label: 'Basic Reader',
      desc: "Read aloud using your browser's default voice.",
      sample: () => togglePlay('basic', BASIC_SAMPLE),
      playing: playing === 'basic',
    },
    {
      mode: 'advanced',
      label: 'Advanced Voice Reader',
      badge: true,
      desc: 'Choose from a range of high-quality voices and languages.',
      sample: () => {
        const voice = voices.find(v => v.value === tts.voice)
        togglePlay(`voice:${tts.voice}`, voice?.audioUrl || BASIC_SAMPLE)
      },
      playing: playing === `voice:${tts.voice}`,
    },
  ]

  return (
    <div className={classes.root}>
      <div className={classes.heading}>Choose your TTS Type</div>
      {options.map(option => {
        const selected = tts.mode === option.mode
        return (
          <div
            key={option.mode}
            className={classes.card}
            style={{
              border: selected
                ? `1.5px solid ${colors.primary}`
                : `1px solid ${colors.border}`,
            }}
          >
            <button
              type="button"
              className={classes.pick}
              onClick={event => pickMode(option.mode, event)}
            >
              <span
                className={classes.radio}
                style={{
                  border: `2px solid ${
                    selected ? colors.primary : colors.subtle
                  }`,
                }}
              >
                <span
                  className={classes.dot}
                  style={{
                    background: selected ? colors.primary : 'transparent',
                  }}
                />
              </span>
              <span className={classes.label}>
                {option.label}
                {option.badge && <Badge type={6} />}
              </span>
            </button>
            <div className={classes.body}>
              <span className={classes.desc}>{option.desc}</span>
              {option.mode === 'advanced' && selected && canUseAdvanced && (
                <div className={classes.selects}>
                  <select
                    className={classes.select}
                    value={tts.language}
                    onChange={event => {
                      const language = event.target.value
                      onChange({
                        ...tts,
                        language,
                        voice: voicesFor(language)[0]?.value || '',
                      })
                    }}
                  >
                    {languages.map(language => (
                      <option key={language.code} value={language.code}>
                        {language.name}
                      </option>
                    ))}
                  </select>
                  <select
                    className={classes.select}
                    value={tts.voice}
                    onChange={event =>
                      onChange({ ...tts, voice: event.target.value })
                    }
                  >
                    {voices.map(voice => (
                      <option key={voice.value} value={voice.value}>
                        {voice.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <button
                type="button"
                className={classes.play}
                onClick={option.sample}
              >
                <Icon
                  icon={
                    option.playing
                      ? 'tabler:player-pause-filled'
                      : 'tabler:player-play-filled'
                  }
                  width={14}
                  height={14}
                />
                {option.playing ? 'Pause' : 'Play'}
              </button>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default TtsSettings
